/**
 * `gamification-core` — projection of field-check `activity` to GameEvents.
 *
 * Gamification is DERIVED from the activity log (`activity_recent` bucket,
 * immutable hash-chain): it does not add tables or mutate the oplog. `applyEvents` is
 * pure and deterministic → the same feed produces the same `PlayerState` (local on
 * client; recomputable on server for anti-fraud).
 *
 * Maps rows of `activity_recent` (entity_type + action + patch) to domain events
 * de dominio declarados en `config.ts`.
 */
import { type ActivityRow, parseActivityPatch } from "../activity/format";
import { applyEvents, initialState } from "./engine";
import { FIELDCHECK_EVENTS, fieldcheckGamificationConfig } from "./config";
import { dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** `client_ts` (honest local time) if present; otherwise `server_ts`. */
function timestampOf(row: Pick<ActivityRow, "client_ts" | "server_ts">): number | null {
  const raw = row.client_ts ?? row.server_ts;
  if (!raw) {
    return null;
  }
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

/** Does the patch record a transition of `status` to `value`? */
function statusChangedTo(row: Pick<ActivityRow, "patch">, value: string): boolean {
  const patch = parseActivityPatch(row.patch);
  const change = patch.status;
  return Boolean(change) && change?.new === value;
}

/**
 * Translates the `activity` feed into gamification events (ascending order by
 * time, as required by the streak reducer). Irrelevant rows are ignored.
 */
export function deriveGameEvents(rows: readonly ActivityRow[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const row of rows) {
    const at = timestampOf(row);
    if (at === null) {
      continue;
    }
    const day = dayKeyUTC(at);
    const base = { at, day, meta: { activityId: row.id } };

    if (row.entity_type === "inspection") {
      if (row.action === "created") {
        events.push({ type: FIELDCHECK_EVENTS.inspectionCreated, ...base });
      } else if (row.action === "updated" && statusChangedTo(row, "completed")) {
        events.push({ type: FIELDCHECK_EVENTS.inspectionCompleted, ...base });
      }
    } else if (
      row.entity_type === "finding" &&
      row.action === "updated" &&
      statusChangedTo(row, "resolved")
    ) {
      events.push({ type: FIELDCHECK_EVENTS.findingResolved, ...base });
    }
  }
  // Feed arrives server_ts DESC; reducer requires ASC chronological order.
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Game state derived from the activity feed. Pure: same feed → same
 * state. Returns XP/level/streaks/badges/quests ready for the UI.
 */
export function deriveGameState(rows: readonly ActivityRow[]): PlayerState {
  const events = deriveGameEvents(rows);
  return applyEvents(initialState(), events, fieldcheckGamificationConfig).state;
}
